import React, { useEffect } from 'react';
import { Form, Input, Select } from 'antd';
import EditableCard from 'components/EditableCard';
import { WarehouseType } from 'graphql/types/WarehouseType';
import { SITE_TYPE_OPTIONS } from 'graphql/types/SiteType';
import { CountryCodeAndPhoneNumber, PhoneInput } from 'components/PhoneInput';
import { useTranslation } from 'react-i18next';
import { useShouldUseVerticalLayout } from '../../../../utils/languageUtils';

interface SiteCenterInfoFormProps {
    data: WarehouseType;
    isEditing?: boolean;
    isNew?: boolean;
    onEdit?: () => void;
    onSave?: (data: WarehouseType) => void;
    onCancel?: () => void;
    title?: string;
    loading?: boolean;
    bordered?: boolean;
}

const SiteCenterInfoForm: React.FC<SiteCenterInfoFormProps> = ({
    data,
    isEditing = false,
    isNew = false,
    onEdit,
    onSave,
    onCancel,
    title = 'Site Info',
    loading = false,
    bordered = false,
}) => {
    const [form] = Form.useForm<WarehouseType & { phone: CountryCodeAndPhoneNumber }>();
    const { t } = useTranslation();
    const useVerticalLayout = useShouldUseVerticalLayout();

    const resetFormData = () => {
        form.setFieldsValue({
            id: data.id,
            name: data.name,
            siteType: data.siteType,
            sortingCode: data.sortingCode,
            contactName: data.contactName,
            extension: data.extension,
            phone: {
                countryCode: data.countryCode || 1,
                phoneNumber: data.phoneNumber,
            },
        });
    };

    useEffect(() => {
        resetFormData();
    }, [data, form]);

    useEffect(() => {
        if (!isEditing) {
            resetFormData();
        }
    }, [isEditing, data]);

    const canEditField = (fieldName: string) => {
        if (isNew) return true;
        if (!isEditing) return false;
        return ['contactName', 'phone', 'extension', 'countryCode'].includes(fieldName);
    };

    const handleSave = () => {
        if (isNew) {
            const values = form.getFieldsValue();
            const newValues: WarehouseType & { phone?: CountryCodeAndPhoneNumber } = {
                ...values,
                phoneNumber: values.phone.phoneNumber,
                countryCode: values.phone.countryCode,
            };
            delete newValues.phone;
            onSave?.(newValues as WarehouseType);
            return;
        }

        onSave?.({
            id: data.id,
            contactName: form.getFieldValue('contactName'),
            phoneNumber: form.getFieldValue('phone')?.phoneNumber,
            countryCode: form.getFieldValue('phone')?.countryCode,
            extension: form.getFieldValue('extension'),
        } as WarehouseType);
    };

    return (
        <EditableCard
            title={title}
            isEditing={isEditing}
            onEdit={onEdit}
            onSave={handleSave}
            onCancel={onCancel}
            loading={loading}
            bordered={bordered}
        >
            <Form
                layout={useVerticalLayout ? 'vertical' : 'horizontal'}
                form={form}
                colon={false}
                labelCol={useVerticalLayout ? undefined : { span: 7 }}
                wrapperCol={useVerticalLayout ? undefined : { span: 17 }}
                className="[&_.ant-form-item-control-input]:w-full [&_.ant-input]:w-full [&_.ant-select]:w-full [&_.ant-select-selector]:w-full [&_.ant-form-item-control-input-content]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item-label]:text-left [&_.ant-form-item-label>label]:text-left"
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4 mb-4 [&_.ant-form-item-control-input-content]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item]:mb-0">
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.siteId')}</span>}
                        name="id"
                    >
                        <Input value={data.id} disabled={!isNew} />
                    </Form.Item>
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.siteName')}</span>}
                        name="name"
                    >
                        <Input value={data.name} disabled={!isNew} />
                    </Form.Item>
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.siteType')}</span>}
                        name="siteType"
                    >
                        <Select
                            value={data.siteType}
                            disabled={!isNew}
                            options={SITE_TYPE_OPTIONS}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.shortingCode')}</span>}
                        name="sortingCode"
                        rules={isNew ? [
                            { required: true, message: 'Sorting code is required' },
                            {
                                pattern: /^[A-Z-]+$/,
                                message: 'Only uppercase letters and hyphens (-) are allowed',
                            },
                        ] : []}
                    >
                        <Input
                            value={data.sortingCode}
                            disabled={!isNew}
                            placeholder={isNew ? 'Enter sorting code (A-Z and - only)' : undefined}
                            onChange={isNew ? (e) => {
                                const value = e.target.value.toUpperCase().replace(/[^A-Z-]/g, '');
                                form.setFieldValue('sortingCode', value);
                            } : undefined}
                        />
                    </Form.Item>
                    {(data.businessType === 1 || data.businessType === 2) && (
                        <Form.Item
                            label={<span className="font-bold">{t('central.sites.details.businessType')}</span>}
                        >
                            <Input
                                value={
                                    data.businessType === 1
                                        ? (t('central.sites.details.businessTypeSorting') as string)
                                        : data.businessType === 2
                                            ? (t('central.sites.details.businessTypeLastMile') as string)
                                            : `${data.businessType}`
                                }
                                disabled
                            />
                        </Form.Item>
                    )}
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.contactName')}</span>}
                        name="contactName"
                        rules={[{ required: true, message: 'Please enter a contact name' }]}
                    >
                        <Input
                            value={data.contactName}
                            disabled={!canEditField('contactName')}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.contactPhone')}</span>}
                        name="phone"
                        rules={[
                            { required: true, message: 'Please enter phone number' },
                            {
                                validator: (_, value) => {
                                    if (!value || !value.phoneNumber) {
                                        return Promise.resolve();
                                    }
                                    if (!/^\d*$/.test(value.phoneNumber)) {
                                        return Promise.reject(new Error('Only numbers are allowed'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <PhoneInput
                            placeholder={t('central.sites.details.contactPhone') || undefined}
                            disabled={!canEditField('phone')}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.ext')}</span>}
                        name="extension"
                        rules={[
                            {
                                pattern: /^\d*$/,
                                message: 'Only numbers are allowed',
                            },
                            {
                                validator: (_, value) => {
                                    if (value && value.length > 10) {
                                        return Promise.reject(new Error('Extension number is too long, maximum 10 characters'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input
                            value={data.extension}
                            disabled={!canEditField('extension')}
                            placeholder={t('central.sites.details.ext') || undefined}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                form.setFieldValue('extension', value);
                            }}
                        />
                    </Form.Item>
                </div>
            </Form>
        </EditableCard>
    );
};

export default SiteCenterInfoForm;
